import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { imgSrc } from "@/lib/api";
import { toast } from "sonner";

export const ProductCard = ({ product, index = 0 }) => {
    const { addItem } = useCart();
    const navigate = useNavigate();
    const variants = product.variants || [];
    const prices = variants.map((v) => (v.price != null ? v.price : product.price));
    const minPrice = prices.length ? Math.min(product.price, ...prices) : product.price;
    const priceVaries = variants.some((v) => v.price != null && v.price !== product.price);

    const quickAdd = (e) => {
        e.preventDefault();
        if (variants.length > 0) {
            navigate(`/product/${product.id}`);
            toast("Choose a color first");
            return;
        }
        addItem(product);
        toast.success(`${product.name} added to your bag`);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="group"
            data-testid={`product-card-${product.id}`}
        >
            <Link to={`/product/${product.id}`} className="block">
                <div className="relative aspect-[3/4] overflow-hidden bg-line/40">
                    <img
                        src={imgSrc(product.image)}
                        alt={product.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    {product.stock === 0 && (
                        <span className="absolute left-0 top-4 bg-espresso px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-sand">
                            Sold out
                        </span>
                    )}
                </div>
                <div className="mt-4 flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-serif text-xl font-medium leading-tight tracking-tight">{product.name}</h3>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-clay">{product.category}</p>
                    </div>
                    <p className="font-mono text-sm" data-testid={`product-price-${product.id}`}>
                        {priceVaries && <span className="mr-1 text-[10px] uppercase tracking-wide text-clay">from</span>}${minPrice.toFixed(2)}
                    </p>
                </div>
                {variants.some((v) => v.image) && (
                    <div className="mt-3 flex items-center gap-1.5" data-testid={`card-swatches-${product.id}`}>
                        {variants.filter((v) => v.image).slice(0, 6).map((v) => (
                            <span key={v.id} title={v.name} className={`h-5 w-4 overflow-hidden border ${v.stock === 0 ? "border-line opacity-30" : "border-line"}`}>
                                <img src={imgSrc(v.image)} alt={v.name} className="h-full w-full object-cover" loading="lazy" />
                            </span>
                        ))}
                        <span className="ml-1 font-mono text-[9px] uppercase tracking-[0.15em] text-clay">
                            {variants.length} color{variants.length === 1 ? "" : "s"}
                        </span>
                    </div>
                )}
            </Link>
            <button
                onClick={quickAdd}
                data-testid={`quick-add-${product.id}`}
                className="mt-3 flex w-full items-center justify-center gap-2 border border-espresso py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] opacity-0 transition-all duration-300 hover:bg-espresso hover:text-sand group-hover:opacity-100"
            >
                <Plus size={12} /> Add to bag
            </button>
        </motion.div>
    );
};
